import React from 'react';
import { Text, TextProps, View, StyleSheet } from 'react-native';
import { useTheme } from '../../hooks/useTheme';

interface LabelProps extends TextProps {
  variant?: 'default' | 'required' | 'optional' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  required?: boolean;
  optional?: boolean;
  error?: boolean;
  htmlFor?: string; // For accessibility
  children: React.ReactNode;
}

/**
 * Label component for forms and general labeling
 */
const Label: React.FC<LabelProps> = ({
  variant = 'default',
  size = 'md',
  weight = 'medium',
  required = false,
  optional = false,
  error = false,
  style,
  children,
  ...props
}) => {
  const { colors } = useTheme();

  const getTextColor = () => {
    if (error || variant === 'error') {
      return '#EF4444';
    }

    switch (variant) {
      case 'success':
        return '#10B981';
      case 'required':
        return colors.text;
      case 'optional':
        return colors.secondary;
      case 'default':
      default:
        return colors.text;
    }
  };

  const getFontSize = () => {
    switch (size) {
      case 'sm':
        return 12;
      case 'md':
        return 14;
      case 'lg':
        return 16;
      default:
        return 14;
    }
  };

  const getFontWeight = () => {
    switch (weight) {
      case 'normal':
        return '400';
      case 'medium':
        return '500';
      case 'semibold':
        return '600';
      case 'bold':
        return '700';
      default:
        return '500';
    }
  };

  const showRequiredIndicator = required || variant === 'required';
  const showOptionalIndicator = optional || variant === 'optional';

  return (
    <View style={styles.container}>
      <Text
        style={[
          {
            color: getTextColor(),
            fontSize: getFontSize(),
            fontWeight: getFontWeight(),
          },
          style,
        ]}
        {...props}
      >
        {children}
        {showRequiredIndicator && (
          <Text style={[styles.indicator, styles.required]}>{' *'}</Text>
        )}
        {showOptionalIndicator && (
          <Text style={[styles.indicator, { color: colors.secondary }]}>
            {' (optional)'}
          </Text>
        )}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 6,
  },
  indicator: {
    fontSize: 14,
  },
  required: {
    color: '#EF4444',
  },
});

export default Label;
